/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'../../common/DataPointActionBase',
	'../api/DataPointActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../widgets/livewidget/nls/StringResources',
	'underscore'
], (DataPointActionBase, DataPointActionsProviderAPI, APIFactory, stringResources, _) => {

	class DrillAction extends DataPointActionBase{
		constructor(options) {
			super(options);
			this.content.getFeature('DataPointActions').registerProvider('DrillAction', this.getAPI());
			this.features = options.features;
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [DataPointActionsProviderAPI]);
			}
			return this._api;
		}

		getDataPointActionList(selections, actionOptions) {
			let actionList = [];
			const dashboardNavigationDrillup = this.features['Dashboard.Icons'].getIcon('dashboard-navigation-drillback');
			const dashboardNavigationDrilldown = this.features['Dashboard.Icons'].getIcon('dashboard-navigation-drilldown');
			if (this._supportAction(selections, actionOptions)) {
				const bDisableDrillDownOption = !this._canDrillDown(selections);
				const bDisableDrillUpOption = !this._canDrillUp(selections);
				actionList.push(...[{
					name: 'drillUp',
					label: stringResources.get('toolbarActionDrillUp'),
					text: stringResources.get('toolbarActionDrillUp'),
					icon: dashboardNavigationDrillup.id,
					type: 'Button',
					closeOnActionApplied: true,
					disabled: bDisableDrillUpOption,
					actions: {
						apply: this.drillUp.bind(this, selections)
					}
				}, {
					name: 'drillDown',
					label: stringResources.get('toolbarActionDrillDown'),
					text: stringResources.get('toolbarActionDrillDown'),
					icon: dashboardNavigationDrilldown.id,
					type: 'Button',
					closeOnActionApplied: true,
					disabled: bDisableDrillDownOption,
					actions: {
						apply: this.drillDown.bind(this, selections)
					}
				}]);
			}
			return actionList;
		}

		_getVisualization() {
			if (!this.visualization) {
				this.visualization = this.content.getFeature('Visualization');
			}
			return this.visualization;
		}

		_getSelectedCategories(selections) {
			return _.compact(_.pluck(selections.dataPoints, 'categories'));
		}

		_supportAction(selections = [], actionOptions = {}) {
			let support = !actionOptions.noFilters || actionOptions.drillOnly;
			if (!support) {
				return support;
			}

			// Support one member from one column.
			const categories = this._getSelectedCategories(selections);
			support = categories && categories.length === 1 && categories[0].length === 1;
			if (!support) {
				return support;
			}

			const columnId = categories[0][0].columnId;
			const dataSource = this._getVisualization().getDataSource();
			const metadataColumn = dataSource.getMetadataColumn(columnId);
			return metadataColumn && (metadataColumn.isHierarchy() || metadataColumn.isNamedSet());
		}

		_getSelectionMappingInfo(selections) {
			const slots = this._getVisualization().getSlots();
			const selectedCategories = this._getSelectedCategories(selections);
			const dataItemId = selectedCategories[0][0].dataItemId;
			const dataItemMappingInfo = slots.getMappingInfo(dataItemId);
			return dataItemMappingInfo;
		}

		_executeDrill(drillDataItem, drillFunction){
			const transaction = this.dashboard.getFeature('Transaction');
			const localTransactionToken = transaction.startTransaction();
			const localFilters = this._getVisualization().getLocalFilters();
			localFilters.removeFilter({
				itemId: drillDataItem.getColumnId()
			}, localTransactionToken);
			return drillFunction(localTransactionToken)
				.finally(() => {
					transaction.endTransaction(localTransactionToken);
				});

		}

		drillUp(selections) {
			const drillDataItem = this._getSelectionMappingInfo(selections).dataItem;
			const memberId = this._getSelectedCategories(selections)[0][0].parentId;
			this._executeDrill(drillDataItem, (transactionToken) => {
				return drillDataItem.drillUp(memberId, transactionToken);
			});
		}

		drillDown(selections) {
			const drillDataItem = this._getSelectionMappingInfo(selections).dataItem;
			const selectedCategories = this._getSelectedCategories(selections);
			const memberId = selectedCategories[0][0].value;
			const parentId = selectedCategories[0][0].parentId;
			this._executeDrill(drillDataItem, (transactionToken) => {
				return drillDataItem.drillDown(memberId, transactionToken, parentId);
			});
		}

		_canDrillDown(selections) {
			return this._getSelectionMappingInfo(selections).dataItem.canDrillDown();
		}

		_canDrillUp(selections) {
			const parentId = this._getSelectedCategories(selections)[0][0].parentId;
			return parentId !== undefined;
		}
	}

	return DrillAction;

});
